/**
 * FeedbackLabeling - Main route orchestrator for labeling feature
 *
 * Coordinates all labeling components and manages the overall workflow
 * Requirement: ES-REQ-057, ES-REQ-058, ES-REQ-059, ES-REQ-084
 */

import React, { useEffect } from 'react';
import styled from 'styled-components';
import { LabelingProvider, useLabeling } from '@/context/labelingContext';
import { LabelingLogin } from '@/components/labeling/LabelingLogin';
import { UserProfile } from '@/components/labeling/UserProfile';
import { FeedbackDisplayPanel } from '@/components/labeling/FeedbackDisplayPanel';
import { LabelingControlsPanel } from '@/components/labeling/LabelingControlsPanel';
import { NavigationPanel } from '@/components/labeling/NavigationPanel';
import { StatusFeedback } from '@/components/labeling/StatusFeedback';
import { SanityRecommendationPanel } from '@/components/labeling/SanityRecommendationPanel';

const Container = styled.div`
  min-height: 100vh;
  background-color: var(--bg-panel);
  width: 100%;
  overflow-x: hidden;
`;

const MainContent = styled.div`
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  padding: 20px;
  box-sizing: border-box;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;
  margin-top: 24px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 18px;
  color: var(--text-muted);
`;

/**
 * Inner component that uses the labeling context
 * This must be separate from the main component to access the context
 */
const FeedbackLabelingContent: React.FC = () => {
  const { state, loadFeedback, fetchCurrentRecommendation } = useLabeling();

  // Load feedback when user authenticates
  useEffect(() => {
    if (state.isAuthenticated && state.token && state.feedback.length === 0 && !state.isLoading) {
      loadFeedback().catch(error => {
        console.error('Failed to load feedback:', error);
      });
    }
  }, [state.isAuthenticated, state.token, state.feedback.length, state.isLoading, loadFeedback]);

  // Fetch Sanity recommendation when current index changes
  useEffect(() => {
    if (state.feedback.length > 0 && state.currentIndex >= 0) {
      fetchCurrentRecommendation().catch(error => {
        console.error('Failed to fetch recommendation:', error);
      });
    }
  }, [state.currentIndex, state.feedback.length, fetchCurrentRecommendation]);

  // Show login screen if not authenticated
  if (!state.isAuthenticated) {
    return (
      <Container>
        <StatusFeedback />
        <MainContent>
          <LabelingLogin />
        </MainContent>
      </Container>
    );
  }

  // Show loading state while fetching feedback
  if (state.isLoading && state.feedback.length === 0) {
    return (
      <Container>
        <StatusFeedback />
        <UserProfile />
        <MainContent>
          <LoadingContainer>
            Loading feedback items...
          </LoadingContainer>
        </MainContent>
      </Container>
    );
  }

  // Main labeling interface
  return (
    <Container>
      <StatusFeedback />
      <UserProfile />
      <MainContent>
        <Grid>
          <LeftColumn>
            <FeedbackDisplayPanel />
            <SanityRecommendationPanel
              recommendation={state.currentRecommendation}
              error={state.sanityError}
              isLoading={state.isLoading && !state.currentRecommendation && !state.sanityError}
            />
            <NavigationPanel />
          </LeftColumn>
          <RightColumn>
            <LabelingControlsPanel />
          </RightColumn>
        </Grid>
      </MainContent>
    </Container>
  );
};

/**
 * Main component with LabelingProvider wrapper
 * Provides context to all child components
 */
export const FeedbackLabeling: React.FC = () => {
  return (
    <LabelingProvider>
      <FeedbackLabelingContent />
    </LabelingProvider>
  );
};

export default FeedbackLabeling;
